import { useEffect } from "react";

import {
  TPA_BRANDING_COOKIE,
  getTpaBrandingRestoreCookie,
  readCustomerCTPA,
  serializeTpaBrandingCookie,
  urlHasAfid,
} from "~/hec/helpers/tpa-branding-restore";
import { useRootLoaderData } from "~/root";
import { getCookieValue } from "~/utils/cookies";

/**
 * SFCC-11069 / TPA-14 — restore the `tpaBranding` cookie for a returning
 * AUTHENTICATED TPA user from their SCAPI `c_TPA` profile attribute when no
 * cookie (and no URL AFID) is present. Mirrors the live headless
 * `setTpaBrandingCookiesFromCustomerData` (`useSyncCookiesFromCustomerData`):
 * runs every authenticated render so it also covers returning-on-valid-session
 * users (not just the explicit login flow).
 *
 * Client-side by design (matches MRT): the restored cookie applies from the
 * NEXT request — the current render isn't retro-branded. Precedence mirrors
 * SFRA: URL AFID → existing cookie → profile restore.
 */
export function useRestoreTpaBranding(): void {
  const { customer } = useRootLoaderData();
  const cTPA = readCustomerCTPA(customer);

  useEffect(() => {
    if (!cTPA) return; // guest / no TPA branding on profile
    if (urlHasAfid(window.location.search)) return; // URL AFID wins (server owns the write)
    // already present — nothing to restore
    if (getCookieValue(document.cookie, TPA_BRANDING_COOKIE) !== null) return;
    const restore = getTpaBrandingRestoreCookie(cTPA);
    if (!restore) return;
    document.cookie = serializeTpaBrandingCookie(restore, {
      secure: window.location.protocol === "https:",
    });
    // Deliberately keyed on `cTPA` (profile identity), not on location: an
    // internal SPA nav to a `?AFID=` URL doesn't need a re-run — that path is a
    // full document load where the server (`buildTpaBrandingCookieHeader`) owns
    // the write, and the cookie-present guard would short-circuit anyway.
  }, [cTPA]);
}
